/**
 * Mock data for authorized representatives
 * This simulates data that would come from an API
 */

export const authorizedReps = [
  {
    id: 1,
    firstName: "Sarah",
    lastName: "Johnson",
    name: "Sarah Johnson",
    email: "sarah.johnson@example.com",
    phone: "(215) 555-0123",
    firm: "Johnson Legal Associates",
    accessLevel: "full",
    dateAdded: "2024-01-15",
    status: "active",
    canSubmitApplications: true,
    canViewFinancials: true,
    canManageProperties: true,
  },
  {
    id: 2,
    firstName: "Michael",
    lastName: "Chen",
    name: "Michael Chen",
    email: "michael.chen@example.com",
    phone: "(215) 555-0456",
    firm: "Chen & Partners LLC",
    accessLevel: "limited",
    dateAdded: "2024-03-20",
    status: "active",
    canSubmitApplications: true,
    canViewFinancials: false,
    canManageProperties: false,
  },
  {
    id: 3,
    firstName: "Emily",
    lastName: "Rodriguez",
    name: "Emily Rodriguez",
    email: "emily.rodriguez@example.com",
    phone: "(215) 555-0789",
    firm: "Rodriguez Law Group",
    accessLevel: "full",
    dateAdded: "2024-05-10",
    status: "active",
    canSubmitApplications: true,
    canViewFinancials: true,
    canManageProperties: true,
  },
  {
    id: 4,
    firstName: "David",
    lastName: "Thompson",
    name: "David Thompson",
    email: "david.thompson@example.com",
    phone: "(215) 555-0321",
    firm: "Thompson Property Management",
    accessLevel: "limited",
    dateAdded: "2024-07-05",
    status: "pending",
    canSubmitApplications: true,
    canViewFinancials: false,
    canManageProperties: false,
  },
  { id: 5,
    firstName: "David",
    lastName: "Thompson",
    name: "David Thompson",
    email: "john.doe@properties.com",
    phone: "(215) 555-0321",
    firm: "Cloudbase services management",
    accessLevel: "limited",
    dateAdded: "2024-07-05",
    status: "pending",
    canSubmitApplications: true,
    canViewFinancials: false,
    canManageProperties: false,
  },
];

/**
 * Access level configuration with display labels and colors
 */
export const accessLevelConfig = {
  full: {
    label: "Full Access",
    color: "#3a833c", // Phanatic Green - Philly Style Guide
    bgColor: "#dcf8d8", // Phanatic Green Light - Philly Style Guide
    description: "Can submit applications, view financials, and manage properties",
  },
  limited: {
    label: "Limited Access",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
    description: "Can only submit applications",
  },
  view_only: {
    label: "View Only",
    color: "#444444", // Dark Gray - Philly Style Guide
    bgColor: "#f0f0f0", // Ghost Gray - Philly Style Guide
    description: "Can only view information, no edit permissions",
  },
};

/**
 * Rep status configuration
 */
export const repStatusConfig = {
  Active: {
    label: "Active",
    color: "#3a833c", // Phanatic Green - Philly Style Guide
    bgColor: "#dcf8d8", // Phanatic Green Light - Philly Style Guide
  },
  Inactive: {
    label: "Inactive",
    color: "#444444", // Dark Gray - Philly Style Guide
    bgColor: "#f0f0f0", // Ghost Gray - Philly Style Guide
  },
};

/**
 * Get access level display information
 * @param {string} levelKey - The access level key
 * @returns {object} Access level configuration object
 */
export function getAccessLevelInfo(levelKey) {
  return accessLevelConfig[levelKey] || {
    label: levelKey,
    color: "#444444",
    bgColor: "#f0f0f0",
    description: "Unknown access level",
  };
}

/**
 * Get rep status display information
 * @param {string} statusKey - The status key
 * @returns {object} Status configuration object
 */
export function getRepStatusInfo(statusKey) {
  return repStatusConfig[statusKey] || {
    label: statusKey,
    color: "#444444",
    bgColor: "#f0f0f0",
  };
}

/**
 * Filter authorized reps by search term
 * @param {string} searchTerm - Search term to filter by
 * @returns {array} Filtered reps
 */
export function filterAuthorizedReps(searchTerm) {
  if (!searchTerm) return authorizedReps;

  const term = searchTerm.toLowerCase();
  return authorizedReps.filter(
    (rep) =>
      rep.name.toLowerCase().includes(term) ||
      rep.firstName.toLowerCase().includes(term) ||
      rep.lastName.toLowerCase().includes(term) ||
      rep.email.toLowerCase().includes(term) ||
      rep.phone.toLowerCase().includes(term) ||
      rep.firm.toLowerCase().includes(term) ||
      rep.accessLevel.toLowerCase().includes(term)
  );
}

/**
 * Get authorized rep by ID
 * @param {number} id - Rep ID
 * @returns {object|null} Rep object or null if not found
 */
export function getAuthorizedRepById(id) {
  return authorizedReps.find((rep) => rep.id === id) || null;
}

/**
 * Mock function to add a new authorized representative
 * @param {object} repData - Representative data
 * @returns {object} Newly created rep with ID
 */
export function addAuthorizedRep(repData) {
  const newRep = {
    id: authorizedReps.length + 1,
    ...repData,
    dateAdded: new Date().toISOString().split("T")[0],
    status: "pending",
  };
  authorizedReps.push(newRep);
  return newRep;
}

/**
 * Mock function to remove an authorized representative
 * @param {number} id - Rep ID to remove
 * @returns {boolean} Success status
 */
export function removeAuthorizedRep(id) {
  const index = authorizedReps.findIndex((rep) => rep.id === id);
  if (index !== -1) {
    authorizedReps.splice(index, 1);
    return true;
  }
  return false;
}

/**
 * Mock function to update rep access level
 * @param {number} id - Rep ID
 * @param {string} newAccessLevel - New access level
 * @returns {object|null} Updated rep or null
 */
export function updateRepAccessLevel(id, newAccessLevel) {
  const rep = getAuthorizedRepById(id);
  if (rep) {
    rep.accessLevel = newAccessLevel;
    // Update permissions based on access level
    if (newAccessLevel === "full") {
      rep.canSubmitApplications = true;
      rep.canViewFinancials = true;
      rep.canManageProperties = true;
    } else if (newAccessLevel === "limited") {
      rep.canSubmitApplications = true;
      rep.canViewFinancials = false;
      rep.canManageProperties = false;
    } else if (newAccessLevel === "view_only") {
      rep.canSubmitApplications = false;
      rep.canViewFinancials = false;
      rep.canManageProperties = false;
    }
    return rep;
  }
  return null;
}

export default authorizedReps;
